// Vendor
import classnames from 'classnames';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';

// Internal
import { Image } from '@src/components/Image/Image';

// Styles
import styles from './styles.module.css';

export interface HeroProps {
  centered?: boolean;
  image?: {
    alt: string;
    fileName: string;
  };
  sectionNumber?: string;
  subTitle?: string;
  title: string;
  titleFulWidth?: boolean;
  video?: {
    placeholder: string;
    src: string;
  };
}

const Hero: React.FC<HeroProps> = (props) => {
  const {
    centered,
    image,
    sectionNumber,
    subTitle,
    title,
    titleFulWidth,
    video
  } = props;

  // Hooks
  const [ref, inView] = useInView({
    triggerOnce: true,
    rootMargin: '-200px 0px'
  });

  // Setup
  const imgAlt = image?.alt;
  const imgFileName = image?.fileName;
  const videoSrc = inView ? video?.src : '';
  const videoPlaceholder = video?.placeholder;

  // Styles
  const classesNumber = `text-lg md:text-2xl font-normal`;
  const classesSubtitle = `text-white text-2xl font-normal tracking-wider`;
  const classesTitle = classnames(
    'text-white text-xl md:text-4xl mb-0 md:mb-2 uppercase tracking-widest',
    {
      'm-auto': centered,
      'max-w-full': titleFulWidth,
      'max-w-2xl': !titleFulWidth
    }
  );

  const classesVideo = `video pointer-events-none w-full`;
  const classesCopy = classnames('text-white absolute z-10', {
    [styles.centered]: centered,
    [styles.bottomLeft]: !centered
  });

  // Markup
  const renderImage = () => {
    if (!image || !imgAlt || !imgFileName) return null;

    return (
      <Image
        alt={imgAlt}
        className="pointer-events-none"
        fileName={imgFileName}
      />
    );
  };
  const renderVideo = () => {
    if (!video) return null;

    return (
      <video
        autoPlay
        className={classesVideo}
        poster={videoPlaceholder}
        src={videoSrc}
        loop
        muted
        playsInline
        ref={ref}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    );
  };

  return (
    <header className="relative">
      {renderVideo()}
      {renderImage()}
      <div className={styles.overlay} />
      <div data-sal="slide-up" data-sal-easing="ease" className={classesCopy}>
        {sectionNumber && <div className={classesNumber}>{sectionNumber}</div>}
        <h1 className={classesTitle}>{title}</h1>
        {subTitle && <h2 className={classesSubtitle}>{subTitle}</h2>}
      </div>
    </header>
  );
};

export { Hero };
